import React, { useEffect, useMemo } from 'react';
import { EntryFields } from 'contentful';
import TextareaAutosize from 'react-textarea-autosize';

import { Accordion, AccordionItem } from './components/Accordion';

import { ContentfulBrand } from 'common/api/contentful/models/ContentfulBrand';
import { createStyles } from 'utils/createStyle';
import { getContentfulBrandApi } from 'common/api/contentful/brands';
import { useApi } from 'common/modules/async-actions/useApi';
import Skeleton from 'react-loading-skeleton';
import { ProductType } from 'common/api/e-comm/models/Product';
import classNames from 'classnames';

export interface ProductDetailTermsProps {
    brandId: number;
    productType: ProductType;
    defaultExpandedFilter?: Function;
    showAffiliateTerms?: boolean;
}

const styles = createStyles({
    termsContent: [
        'w-full text-sm mt-1 text-grey-1 font-normal resize-none',
        { lineHeight: '22px' },
    ],
});

const getContent = (entry: EntryFields.RichText) => {
    return (
        entry &&
        entry.content &&
        entry.content.length > 0 &&
        entry.content[0] &&
        entry.content[0].content &&
        entry.content[0].content.length > 0 &&
        entry.content[0].content[0].value
    );
};

const termsAndInfoTitles: { [key: string]: string } = {
    generalInformation: 'General Information',
    giftCardRedemptionInstructions: 'Gift Card Redemption Instructions',
    eGiftCardRedemptionInstructions: 'eGift Card Redemption Instructions',
    giftCardTermsAndConditions: 'Gift Card Terms and Conditions',
    eGiftCardTermsAndConditions: 'eGift Card Terms And Conditions',
};

const sortedItemValue: { [key: string]: number } = {
    generalInformation: 0,
    eGiftCardRedemptionInstructions: 1,
    giftCardRedemptionInstructions: 2,
    eGiftCardTermsAndConditions: 3,
    giftCardTermsAndConditions: 4,
};

const convertToAccordionItems = (entry: ContentfulBrand): AccordionItem[] => {
    return Object.entries(entry)
        .map((item) => ({ key: item[0], value: getContent(item[1]) }))
        .map((item) => ({
            value: item.key,
            title: (
                <span className="font-semibold text-xs leading-1">
                    {termsAndInfoTitles[item.key]}
                </span>
            ),
            content: item.value ? (
                <div className="px-3">
                    <TextareaAutosize
                        className={styles.termsContent}
                        readOnly={true}
                        value={item.value.toString()}
                    />
                </div>
            ) : (
                ''
            ),
        }))
        .filter((item) => item.content);
};

const filterTermsForCurrentTab = (
    items: AccordionItem[],
    currentTabName: ProductType
): AccordionItem[] => {
    if (currentTabName === ProductType.EGiftCard) {
        return items.filter(
            (item) =>
                item.value.toLowerCase().startsWith('egiftcard') ||
                item.value === 'generalInformation'
        );
    }

    return items.filter(
        (item) =>
            item.value.toLowerCase().startsWith('giftcard') || item.value === 'generalInformation'
    );
};

/**
 * The following *Filter functions are exported and should be
 * passed in to the ProductDetailTerms component to select which
 * accordion section should be expanded by default.
 */
export const getTermsAndConditionsFilter = (productType: ProductType) => {
    if (productType === ProductType.EGiftCard) {
        return 'eGiftCardTermsAndConditions';
    }

    return 'giftCardTermsAndConditions';
};

export const getDetailsAndExclusionsFilter = () => 'details-and-exclusions';

//disabling linting so that this function will match the function signature of other filters
//even though this filter doesn't depend on the productType
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getGeneralInformationFilter = (_: ProductType) => {
    return 'generalInformation';
};

export const ProductDetailTerms = ({
    brandId,
    productType,
    defaultExpandedFilter,
    showAffiliateTerms = false,
}: ProductDetailTermsProps) => {
    const [loadContentfulBrand, loadContentfulBrandAsyncState, loadContentfulBrandResponse] =
        useApi(getContentfulBrandApi);

    useEffect(() => {
        if (brandId > 0) {
            loadContentfulBrand(brandId);
        }
    }, [brandId, loadContentfulBrand]);

    const terms = useMemo(
        () => loadContentfulBrandResponse?.data || undefined,
        [loadContentfulBrandResponse]
    );

    const brandTerms = useMemo(
        () => (terms?.items?.length && terms.items[0].fields) || undefined,
        [terms]
    );

    const productTypeTermsAndInfos = useMemo(() => {
        if (!brandTerms) {
            return [];
        }
        return [
            ...filterTermsForCurrentTab(convertToAccordionItems(brandTerms), productType)
                .map((item) => ({ ...item, position: sortedItemValue[item.value] }))
                .sort((a, b) => a.position - b.position),
            showAffiliateTerms
                ? {
                      title: (
                          <span className="font-semibold text-xs leading-1">
                              Details & Exclusions
                          </span>
                      ),
                      value: 'details-and-exclusions',
                      content: (
                          <div
                              className={classNames(styles.termsContent, 'px-3')}
                              dangerouslySetInnerHTML={{
                                  __html: `<p>When you buy through the link on RaiseRight, we may earn a commission.</p>`,
                              }}
                          ></div>
                      ),
                  }
                : null,
        ].filter(Boolean) as AccordionItem[];
    }, [brandTerms, productType, showAffiliateTerms]);

    const defaultItemValue = useMemo(() => {
        if (defaultExpandedFilter) {
            return defaultExpandedFilter(productType);
        }
        return null;
    }, [productType, defaultExpandedFilter]);

    // if it is loading show the animation to the user
    if (loadContentfulBrandAsyncState.loading) {
        return <Skeleton height={20} />;
    }

    // if the brandId is invalid just don't render anything
    if (!productTypeTermsAndInfos) {
        return null;
    }

    // render the brand terms/infos for the product type
    return (
        <Accordion items={productTypeTermsAndInfos} defaultExpandedItemValue={defaultItemValue} />
    );
};
